export interface Email {
  id: string;
  subject: string;
  from: string;
  body: string;
  receivedAt: string;
  read: boolean;
  category: string;
  disputeHook?: DisputeHook;
  loggedAsDispute?: boolean;
  archived?: boolean;
  forwardedToCollections?: boolean;
  repliedTo?: boolean;
  replyBody?: string;
  replyChallenge?: ReplyChallengeRef;
}

/** Forward-declared so EventBus stays decoupled from content. The content
 *  module re-exports a richer ReplyChallenge that's structurally compatible. */
export interface ReplyChallengeRef {
  lookupHint: string;
  options: ReadonlyArray<{ id: string; body: string; correct: boolean }>;
}

export type SlackAuthor = 'jamie' | 'cfo' | 'marcus';

export interface SlackMessage {
  id: string;
  author: SlackAuthor;
  body: string;
  /** Fictional-clock minutes at which this message should fire (e.g. 540 = 9:00 AM). */
  fictionalMinutes: number;
}

export type Term =
  | 'aging-bucket'
  | 'dunning'
  | 'cash-application'
  | 'short-pay'
  | 'dispute'
  | 'promise-to-pay'
  | 'write-off'
  | 'dso';

export type DunningTemplate =
  | 'first-notice'
  | 'past-due'
  | 'final-notice'
  | 'collections-referral';

export type ShortPayChoice = 'apply-and-chase' | 'credit-memo' | 'reject';

export type DisputeType = 'po-mismatch' | 'amount' | 'billing-error' | 'other';

export type CallOutcome = 'success' | 'partial_success' | 'fail';

export interface DisputeHook {
  customer: string;
  invoiceId: string;
  amountSuggested: number;
}

export interface CashApplicationAllocation {
  invoiceId: string;
  amount: number;
}

export type WindowId = 'inbox' | 'slack' | 'spreadsheet' | 'field-guide' | 'payments';

export interface PendingPayment {
  id: string;
  customer: string;
  amount: number;
  remittance: string;
  arrivedAtMs: number;
  expiresAtMs: number;
}

export interface BadgePayload {
  label: string;
  count: number;
  pulse: boolean;
  iconColor: number;
}

export type EventPayloads = {
  'clock:tick': { fictionalMinutes: number; realElapsedMs: number };
  'email:received': { email: Email };
  'inbox:unread-changed': { count: number };
  'slack:message-received': { message: SlackMessage };
  'slack:unread-changed': { count: number };
  'day:ended': { realElapsedMs: number; unreadCount: number };
  'beat:triggered': { beatId: string; term: Term };
  'teach:requested': { term: Term };
  'teach:dismissed': { term: Term };
  'term:unlocked': { term: Term };
  'dunning:opened': { customer: string };
  'dunning:open-for': { customer: string; bucket: string };
  'dunning:sent': {
    customer: string;
    template: DunningTemplate;
    correctForBucket: boolean;
  };
  'payment:received': {
    wireId: string;
    customer: string;
    amount: number;
  };
  'cash-application:opened': { wireId: string };
  'cash-application:sent': {
    wireId: string;
    customer: string;
    correctCustomer: boolean;
    allocations: readonly CashApplicationAllocation[];
    unapplied: number;
  };
  'short-pay:opened': { wireId: string; customer: string };
  'short-pay:resolved': {
    wireId: string;
    customer: string;
    paidAmount: number;
    invoiceAmount: number;
    invoiceId: string;
    choice: ShortPayChoice;
  };
  'dispute:email-action-clicked': { emailId: string; hook: DisputeHook };
  'dispute:form-opened': { emailId: string; customer: string; invoiceId: string };
  'dispute:resolved': {
    emailId: string;
    customer: string;
    invoiceId: string;
    amount: number;
    disputeType: DisputeType;
    reason: string;
  };
  'window:minimize-requested': { windowId: WindowId };
  'window:restore-requested':  { windowId: WindowId };
  'window:minimized':          { windowId: WindowId; order: number };
  'window:restored':           { windowId: WindowId };
  'window:badge-changed':      { windowId: WindowId; badge: BadgePayload };
  'window:focus-requested':    { windowId: WindowId };
  'window:focus-changed':      { focused: WindowId | null; stack: readonly WindowId[] };
  'phone:call-incoming':       { callId: string; customer: string; reason: string };
  'phone:call-answered':       { callId: string };
  'phone:call-ended':           { callId: string; outcome: CallOutcome };
  'ptp:recorded':               { customer: string; amount: number; date: string | null };
  'write-off:opened':           { invoiceId: string; customer: string; amount: number };
  'write-off:approved':         { invoiceId: string; customer: string; amount: number };
  'write-off:declined':         { invoiceId: string; customer: string; amount: number };
  'dso:opened':                 { dso: number };
  'dso:dismissed':              { dso: number };
  'field-guide:focus-term':     { term: Term };
  'field-guide:opened':         Record<string, never>;
  'field-guide:closed':         Record<string, never>;
  'aging-tick:fired':           { customer: string; fromBucket: string; toBucket: string; amount: number };
  'procedural:call-rang':       { caller: string };
  'endless:ended':              { reason: 'dso' | 'unread' | 'aging-90'; value: number };
  'payment:queued':             { payment: PendingPayment };
  'payment:expired':            { payment: PendingPayment };
  'cash-application:open-for':  { payment: PendingPayment };
  'cash-application:closed':    { wireId: string };
  'score:changed':              { score: number; delta: number; reason: string };
  'email:action-taken':         { emailId: string; action: EmailAction; correctness: EmailActionCorrectness; replyBody?: string };
  'ar-ledger:changed':          { reason: 'cash-applied' | 'write-off' };
  'settings:rates-changed':     { rates: { email: number; payment: number; call: number; 'aging-tick': number; paused: boolean } };
  'settings:open-requested':    Record<string, never>;
};

export type EmailAction = 'reply' | 'archive' | 'forward-collections' | 'acknowledge';
export type EmailActionCorrectness = 'correct' | 'acceptable' | 'wrong';

export type EventName = keyof EventPayloads;

type Handler<E extends EventName> = (payload: EventPayloads[E]) => void;

export class EventBus {
  private readonly handlers = new Map<EventName, Set<Handler<EventName>>>();

  on<E extends EventName>(event: E, handler: Handler<E>): void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as Handler<EventName>);
  }

  off<E extends EventName>(event: E, handler: Handler<E>): void {
    this.handlers.get(event)?.delete(handler as Handler<EventName>);
  }

  emit<E extends EventName>(event: E, payload: EventPayloads[E]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const handler of set) {
      (handler as Handler<E>)(payload);
    }
  }

  clear(): void {
    this.handlers.clear();
  }
}
